import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight perché l'accordion è gestito esternamente
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';

interface StyleEditorSectionProps {
  layer: LayerBase & LayerStyleProperties;
  updateLayerStyle: (
    layerId: string,
    propertyName: keyof LayerStyleProperties,
    value: StyleValue
  ) => void;
  isDisabled: boolean;
}

export const FillLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  return (
    <>
      {/* fill-sort-key */}
      <StylePropertyInput
        key={`${layer.id}-fill-sort-key`}
        layerId={layer.id}
        propertyName="fillSortKey"
        label="Sort Key"
        currentValue={layer.fillSortKey}
        propertyType="number"
        numberConfig={{}}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* visibility */}
      <StylePropertyInput
        key={`${layer.id}-visibility`}
        layerId={layer.id}
        propertyName="visibility"
        label="Visibility"
        currentValue={layer.visibility}
        propertyType="select"
        selectOptions={[{ value: 'visible', label: 'Visible' }, { value: 'none', label: 'None' }]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

export const FillPaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  return (
    <>
      {/* fill-antialias */}
      <StylePropertyInput
        key={`${layer.id}-fill-antialias`}
        layerId={layer.id}
        propertyName="fillAntialias"
        label="Antialias"
        currentValue={layer.fillAntialias}
        propertyType="boolean"
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* fill-color */}
      <StylePropertyInput
        key={`${layer.id}-fill-color`}
        layerId={layer.id}
        propertyName="fillColor"
        label="Fill Color"
        currentValue={layer.fillColor}
        propertyType="color"
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* fill-opacity */}
      <StylePropertyInput
        key={`${layer.id}-fill-opacity`}
        layerId={layer.id}
        propertyName="fillOpacity"
        label="Fill Opacity"
        currentValue={layer.fillOpacity}
        propertyType="range"
        numberConfig={{ min: 0, max: 1, step: 0.05 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
        hideNumericValueDisplay={true}
      />
      {/* fill-outline-color */}
      <StylePropertyInput
        key={`${layer.id}-line-color`}
        layerId={layer.id}
        propertyName="lineColor"
        label="Outline Color"
        currentValue={layer.lineColor}
        propertyType="color"
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={(layerId, _propertyName, value) => updateLayerStyle(layerId, "lineColor", value)}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* fill-pattern */}
      <StylePropertyInput
        key={`${layer.id}-fill-pattern`}
        layerId={layer.id}
        propertyName="fillPattern"
        label="Pattern"
        currentValue={layer.fillPattern}
        propertyType="text"
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* fill-translate */}
      <StylePropertyInput
        key={`${layer.id}-fill-translate`}
        layerId={layer.id}
        propertyName="fillTranslate"
        label="Translate (X, Y)"
        currentValue={layer.fillTranslate}
        propertyType="array"
        numberConfig={{}}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* fill-translate-anchor */}
      <StylePropertyInput
        key={`${layer.id}-fill-translate-anchor`}
        layerId={layer.id}
        propertyName="fillTranslateAnchor"
        label="Translate Anchor"
        currentValue={layer.fillTranslateAnchor}
        propertyType="select"
        selectOptions={[{ value: 'map', label: 'Map' }, { value: 'viewport', label: 'Viewport' }]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

// L'esportazione di default di FillStyleEditor non è più necessaria
// se LayerOptionsPanel importa direttamente FillLayoutProperties e FillPaintProperties.
// Potrebbe essere rimosso o restituire null per evitare errori se ancora importato altrove.
const FillStyleEditor: React.FC<StyleEditorSectionProps> = () => {
  // Questo componente non renderizza più direttamente l'intera UI.
  // La logica è stata spostata in FillLayoutProperties e FillPaintProperties.
  return null;
};

export default FillStyleEditor;